import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { GridFilterModel } from '@mui/x-data-grid-pro';

import TableInstance from '@/shared/constants/tableInstance';

type DataGridFilterContextType = {
  dataGridFilter: {
    [key in TableInstance]?: GridFilterModel;
  };
  onSaveDataGridFilter: (
    tableInstance: TableInstance,
    filter: GridFilterModel,
  ) => void;
  addCurrentLocationToLocationsList: (currentLocation: string) => void;
};

export const DataGridFilterContext = createContext<DataGridFilterContextType>({
  dataGridFilter: {},
  onSaveDataGridFilter: () => {},
  addCurrentLocationToLocationsList: () => {},
});

type Props = {
  children: ReactNode;
};

const DataGridFilterProvider = ({ children }: Props) => {
  const [locationsList, setLocationsList] = useState<string[]>([]);
  const [dataGridFilter, setDataGridFilter] = useState<
    DataGridFilterContextType['dataGridFilter']
  >({});

  const onSaveDataGridFilter = useCallback(
    (tableInstance: TableInstance, filter: unknown) => {
      setDataGridFilter((prevDataGridFilter) => ({
        ...prevDataGridFilter,
        [tableInstance]: filter,
      }));
    },
    [],
  );

  const addCurrentLocationToLocationsList = useCallback(
    (currentLocation: string) => {
      const locationFirstSegment = currentLocation.match(/\/\w+/g)?.[0] || '';
      const LOCATION_LIST_CAPACITY = 2;

      setLocationsList((prevValue) => {
        if (prevValue.length >= LOCATION_LIST_CAPACITY) {
          return [prevValue[0], locationFirstSegment];
        }
        return [...prevValue, locationFirstSegment];
      });
    },
    [],
  );

  useEffect(() => {
    if (locationsList.length > 1) {
      const [firstLocationInList, ...restLocations] = locationsList;
      const isTheSameLocation = restLocations.every(
        (location) => location === firstLocationInList,
      );

      if (!isTheSameLocation) {
        setDataGridFilter({});
        setLocationsList([]);
      }
    }
  }, [locationsList]);

  return (
    <DataGridFilterContext.Provider
      value={{
        dataGridFilter,
        onSaveDataGridFilter,
        addCurrentLocationToLocationsList,
      }}
    >
      {children}
    </DataGridFilterContext.Provider>
  );
};

export default DataGridFilterProvider;
